"use client";

import { EVENT_TYPES } from '@/utils/constants';
import { logEvent } from '@/utils/helpers/analyticsHelpers';
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error?: Error;
}

/**
 * Error boundary specifically for impression tracking components
 * Prevents impression tracking errors from breaking the entire app
 */
export class ImpressionTrackingErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.warn('Impression tracking error caught by boundary:', {
            error: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
        });

        this.props.onError?.(error, errorInfo);

        if (process.env.NODE_ENV === 'production') {
            logEvent(null, EVENT_TYPES.IMPRESSION_TRACKING_ERROR, {
                error_message: error.message,
                error_name: error.name,
                error_stack: error.stack,
                component_stack: errorInfo.componentStack,
                timestamp: Date.now(),
                user_agent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
                url: typeof window !== 'undefined' ? window.location.href : 'unknown',
                boundary_type: 'impression_tracking',
            });
        }
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback || this.props.children;
        }

        return this.props.children;
    }
}

/**
 * Higher-order component wrapper for impression tracking error boundary
 */
export function withImpressionTrackingErrorBoundary<P extends object>(
    Component: React.ComponentType<P>,
    fallbackComponent?: React.ComponentType<P>
) {
    const WrappedComponent = (props: P) => {
        const FallbackComponent = fallbackComponent;
        return (
            <ImpressionTrackingErrorBoundary
                fallback={FallbackComponent ? <FallbackComponent {...props} /> : undefined}
            >
                <Component {...props} />
            </ImpressionTrackingErrorBoundary>
        );
    };

    WrappedComponent.displayName = `withImpressionTrackingErrorBoundary(${Component.displayName || Component.name})`;

    return WrappedComponent;
}

/**
 * Hook to provide error boundary context (for function components that need error info)
 */
export function useImpressionTrackingErrorHandler() {
    const handleError = React.useCallback((error: Error, context?: string) => {
        console.warn(`Impression tracking error${context ? ` in ${context}` : ''}:`, error);

        if (process.env.NODE_ENV === 'production') {
            logEvent(null, EVENT_TYPES.IMPRESSION_TRACKING_ERROR, {
                error_message: error.message,
                error_name: error.name,
                error_stack: error.stack,
                context: context || 'unknown',
                timestamp: Date.now(),
                user_agent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
                url: typeof window !== 'undefined' ? window.location.href : 'unknown',
                boundary_type: 'hook_handler',
            });
        }
    }, []);

    return { handleError };
}